import type {
  AccountLink,
  AdminUser,
  AdminUsersResponse,
  AuthMethods,
  CreateUserInput,
  CreateUserResult,
  LaunchTarget,
  Me,
  SignupInput,
  Tool,
  UpdateUserInput,
  VerifySignupResult,
} from './types';

/** How the administration listing is narrowed. There is one list,
 *  platform-wide: with no organisations there is no smaller scope. */
export interface UserQuery {
  includeArchived?: boolean;
  /** Limit to these states. Empty means everyone but the archived. */
  statuses?: string[];
  /** Matches email, name or company. */
  search?: string;
}

function userQuery(options: UserQuery = {}): string {
  const params = new URLSearchParams();
  if (options.includeArchived) params.set('include_archived', 'true');
  if (options.statuses?.length) params.set('status', options.statuses.join(','));
  if (options.search?.trim()) params.set('q', options.search.trim());
  const q = params.toString();
  return q ? `?${q}` : '';
}

/**
 * API base. Empty by default: the Vite dev server proxies /api to the backend
 * and production serves both from one origin, which is what lets the session
 * cookie stay SameSite=Lax. Set VITE_API_URL only for a genuinely
 * cross-origin deployment, and add that origin to ALLOWED_ORIGINS.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** An error carrying the server's machine-readable code. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  /** True when the caller is not signed in (or the session just expired). */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string };
}

// `body` is replaced rather than intersected: callers pass a plain object and
// this function does the JSON encoding, so RequestInit's BodyInit type would
// only get in the way.
type JsonRequestInit = Omit<RequestInit, 'body'> & { body?: unknown };

async function request<T>(path: string, init: JsonRequestInit = {}): Promise<T> {
  const { body, headers, ...rest } = init;

  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    // The session lives in an HttpOnly cookie; without this the browser would
    // not send it and every call would look unauthenticated.
    credentials: 'include',
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body means something in front of the API answered (a proxy,
      // a load balancer). Surface it as a generic failure rather than crashing.
      if (!response.ok) {
        throw new ApiError(response.status, 'unexpected_response', 'The server returned an unexpected response.');
      }
      throw new ApiError(response.status, 'unexpected_response', 'The server returned an unexpected response.');
    }
  }

  if (!response.ok) {
    const envelope = payload as ErrorEnvelope | undefined;
    throw new ApiError(
      response.status,
      envelope?.error?.code ?? 'unknown',
      envelope?.error?.message ?? 'Something went wrong.',
    );
  }
  return payload as T;
}

export const api = {
  /** Signs in and returns the same payload as `me()`. */
  login: (email: string, password: string) =>
    request<Me>('/api/v1/auth/login', { method: 'POST', body: { email, password } }),

  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST' }),

  /** What the sign-in page should offer. */
  authMethods: () => request<AuthMethods>('/api/v1/auth/methods'),

  /** Starts a sign-up. Always resolves, whether or not the address is free:
   *  the server deliberately gives the same answer either way. */
  signup: (input: SignupInput) =>
    request<void>('/api/v1/auth/signup', { method: 'POST', body: input }),

  /** Proves the address. A company address is signed in on the spot; a free
   *  mailbox comes back as "pending_approval" with nothing to boot. */
  verifySignup: (token: string) =>
    request<VerifySignupResult>('/api/v1/auth/verify-signup', {
      method: 'POST',
      body: { token },
    }),

  me: () => request<Me>('/api/v1/me'),

  /** Always resolves, whether or not the address is known — the server
   *  deliberately gives the same answer either way. */
  forgotPassword: (email: string) =>
    request<void>('/api/v1/auth/forgot-password', { method: 'POST', body: { email } }),

  /** Resolves the account behind an invitation or reset link. */
  inspectLink: (token: string) =>
    request<AccountLink>(`/api/v1/auth/links/${encodeURIComponent(token)}`),

  /** Redeems a link: sets the password and signs the caller in. */
  setPasswordViaLink: (token: string, newPassword: string) =>
    request<Me>('/api/v1/auth/set-password', {
      method: 'POST',
      body: { token, new_password: newPassword },
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/v1/auth/password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),

  tools: () => request<{ tools: Tool[] }>('/api/v1/tools'),

  tool: (key: string) => request<Tool>(`/api/v1/tools/${encodeURIComponent(key)}`),

  /** Mints a one-off address that opens a tool as the caller.
   *
   *  A POST rather than a link: the response carries a credential, so it must
   *  not be something a browser prefetches or a crawler follows, and it is
   *  only good for as long as this session is. */
  launchTool: (key: string) =>
    request<LaunchTarget>(`/api/v1/tools/${encodeURIComponent(key)}/launch`, { method: 'POST' }),

  // ── Administration ────────────────────────────────────────────────────────
  // One tier: EJOT's own operators. These answer 404 rather than 403 for
  // anyone else, so the tier does not announce itself.

  adminUsers: (query: UserQuery = {}) =>
    request<AdminUsersResponse>(`/api/v1/admin/users${userQuery(query)}`),

  createUser: (input: CreateUserInput) =>
    request<CreateUserResult>('/api/v1/admin/users', { method: 'POST', body: input }),

  updateUser: (id: string, input: UpdateUserInput) =>
    request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    }),

  /** Lets a waiting sign-up in, and grants every released tool. */
  approveUser: (id: string) =>
    request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
    }),

  /** Turns a waiting sign-up down. The row stays, so the address stays taken
   *  and the decision can be reversed later. */
  rejectUser: (id: string) =>
    request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
    }),

  setGrants: (id: string, toolKeys: string[]) =>
    request<AdminUser>(`/api/v1/admin/users/${encodeURIComponent(id)}/grants`, {
      method: 'PUT',
      body: { tool_keys: toolKeys },
    }),

  /** Mails a fresh invitation (or a reset link, for an active account).
   *  An administrator can offer a way in; they never choose the password. */
  sendInvitation: (id: string) =>
    request<void>(`/api/v1/admin/users/${encodeURIComponent(id)}/invite`, { method: 'POST' }),

  revokeSessions: (id: string) =>
    request<void>(`/api/v1/admin/users/${encodeURIComponent(id)}/sessions/revoke`, {
      method: 'POST',
    }),
};
